import { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";
import { Clock, ClipboardList, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";

const BLUE = "#0F62FE";
const GREEN = "#24A148";
const YELLOW = "#F1C21B";
const RED = "#FA4D56";
const SLATE = "#94A3B8";

const STATUS_COLORS = { pendiente: YELLOW, completado: GREEN, escalado: RED };
const PRIORITY_COLORS = { alta: RED, media: BLUE, baja: SLATE };
const STATUS_LABELS = { pendiente: "Pendiente", completado: "Completado", escalado: "Escalado" };
const PRIORITY_LABELS = { alta: "Alta", media: "Media", baja: "Baja" };
const TYPE_LABELS = { correctivo: "Correctivo", preventivo: "Preventivo" };

function StatCard({ icon: Icon, label, value, sub, color, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 transition-shadow duration-200 hover:shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.15em] text-slate-500">{label}</p>
        <span className="rounded-md p-1.5" style={{ backgroundColor: `${color}1A`, color }}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <p className="mt-2 font-heading text-3xl font-black tracking-tight text-slate-900">{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid={testId}>
      <h3 className="font-heading mb-4 text-base font-bold text-slate-900">{title}</h3>
      <div className="h-64">{children}</div>
    </div>
  );
}

function EmptyChart({ text }) {
  return <div className="flex h-full items-center justify-center text-sm text-slate-400">{text}</div>;
}

export default function Reports() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/summary").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === null) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4" data-testid="reports-loading">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 w-full rounded-lg" />)}
      </div>
    );
  }
  if (data === false) {
    return <p className="text-sm text-red-600" data-testid="reports-error">No se pudieron cargar los reportes.</p>;
  }

  const byStatus = data.by_status.map((s) => ({ ...s, name: STATUS_LABELS[s.status] || s.status }));
  const byPriority = data.by_priority.map((p) => ({ ...p, name: PRIORITY_LABELS[p.priority] || p.priority }));
  const byType = data.avg_by_service_type.map((t) => ({ ...t, name: TYPE_LABELS[t.service_type] || t.service_type }));

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={ClipboardList} label="Total de órdenes" value={data.total_orders} color={BLUE} testId="stat-total-orders" />
        <StatCard icon={AlertTriangle} label="Órdenes abiertas" value={data.open_orders} color={YELLOW} testId="stat-open-orders" />
        <StatCard icon={CheckCircle2} label="Completadas" value={data.completed_orders} color={GREEN} testId="stat-completed-orders" />
        <StatCard
          icon={Clock}
          label="Tiempo prom. resolución"
          value={data.avg_resolution_hours != null ? `${data.avg_resolution_hours}h` : "—"}
          sub="Promedio general de órdenes completadas"
          color={BLUE}
          testId="stat-avg-resolution"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Tiempo promedio de resolución por técnico (horas)" testId="chart-avg-by-technician">
          {data.avg_by_technician.length === 0 ? (
            <EmptyChart text="Sin órdenes completadas con tiempos registrados." />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.avg_by_technician} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="technician" width={110} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v, name, item) => [`${v}h (${item.payload.completed} órdenes)`, "Promedio"]} />
                <Bar dataKey="avg_hours" fill={BLUE} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Tiempo promedio por tipo de servicio (horas)" testId="chart-avg-by-type">
          {byType.length === 0 ? (
            <EmptyChart text="Sin órdenes completadas con tiempos registrados." />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byType} margin={{ left: 0, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v, name, item) => [`${v}h (${item.payload.completed} órdenes)`, "Promedio"]} />
                <Bar dataKey="avg_hours" fill={GREEN} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Órdenes por estado" testId="chart-by-status">
          {byStatus.length === 0 ? (
            <EmptyChart text="Sin órdenes registradas." />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={byStatus} dataKey="count" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {byStatus.map((s) => (
                    <Cell key={s.status} fill={STATUS_COLORS[s.status] || SLATE} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Órdenes por prioridad" testId="chart-by-priority">
          {byPriority.length === 0 ? (
            <EmptyChart text="Sin órdenes registradas." />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={byPriority} dataKey="count" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {byPriority.map((p) => (
                    <Cell key={p.priority} fill={PRIORITY_COLORS[p.priority] || SLATE} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <ChartCard title="Volumen de órdenes por mes (últimos 6 meses)" testId="chart-monthly-volume">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data.monthly_volume} margin={{ left: 0, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v) => [v, "Órdenes"]} />
            <Line type="monotone" dataKey="orders" stroke={BLUE} strokeWidth={2.5} dot={{ r: 4, fill: BLUE }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
